import { ActionIcon, Tooltip, Text, Group } from "@mantine/core"
import { IconBriefcaseOff, IconBriefcase } from "@tabler/icons-react"
import _ from "lodash"
import { useState, useMemo } from "react"

import { BudgetsResponses } from "@costory/types/endpoints/budgets"
import { BudgetStatus, CostType, Currency } from "@costory/types/prisma-client"

import dayjs from "@costory/shared/dayjs"

import UserAvatar from "@costory/front/components/icons/UserAvatar"
import { AllocationsRow } from "@costory/front/pages/Budgets/components/AllocationsRow"
import { TableRowFormValues } from "@costory/front/pages/Budgets/components/BudgetTable"
import { BusinessMetricRow } from "@costory/front/pages/Budgets/components/BusinessMetricRow"
import { FormatedTd } from "@costory/front/pages/Budgets/components/FormatedTd"
import { RevisionRow } from "@costory/front/pages/Budgets/components/RevisionRow"
import { SectionHead } from "@costory/front/pages/Budgets/components/SectionHead"
import {
  BudgetTableMode,
  mediumGrayBorder,
} from "@costory/front/pages/Budgets/consts"
import { sumYTDOrNull, sumOrNull } from "@costory/front/pages/Budgets/utils"

export const EditableBudgetTableRow = ({
  actualCosts,
  virtualDimensionValue,
  virtualDimensionId,
  startDate,
  forecastedCosts,
  values: { allocations, ownerId },
  revisions,
  onChange,
  currentRevisionName,
  mode,
  formData,
  currency,
  costType,
}: {
  actualCosts: (number | null)[]
  virtualDimensionValue: string
  forecastedCosts: (number | null)[]
  values: TableRowFormValues
  onChange: (values: TableRowFormValues) => void
  virtualDimensionId: string
  startDate: string
  revisions: {
    name: string
    values: number[]
    versionStatus: BudgetStatus
    lastEdited: Date
  }[]
  currentRevisionName: string
  mode: BudgetTableMode
  formData: BudgetsResponses.BudgetFormData
  currency: Currency
  costType: CostType
}) => {
  const deltas =
    allocations?.map((allocation, i) => {
      const actual = actualCosts[i]
      if (actual === null) {
        return undefined
      }
      return allocation - actual
    }) ?? new Array(12).fill(null)
  const [businessMetricId, setBusinessMetricId] = useState<string | null>(null)

  const actualCostsYTD = sumYTDOrNull(actualCosts)
  const deltasYTD = sumYTDOrNull(deltas)
  const forecastedCostsYTD = sumYTDOrNull(forecastedCosts)

  const forecastedCostsTotal = sumOrNull(forecastedCosts)
  const actualCostsTotal = sumOrNull(actualCosts)
  const deltasTotal = sumOrNull(deltas)

  const onClickCompareToBusinessMetric = () => {
    setBusinessMetricId((prev) => (prev === null ? "" : null))
  }

  const extraLines = Math.max(
    0,
    (revisions?.length ?? 0) - (mode === BudgetTableMode.Edit ? 1 : 0),
  )

  // Get owner from VD rule for this virtual dimension value
  const vdOwner = useMemo(() => {
    const selectedVD = formData.availableVirtualDimensions.find(
      (vd) => vd.id === virtualDimensionId,
    )
    if (!selectedVD?.rules) return null

    // Find rule that maps to this virtual dimension value
    for (const rule of selectedVD.rules) {
      if (!rule.allocation) continue

      const allocation = rule.allocation as {
        allocationType: string
        dimensionValue?: string
        reAllocationParams?: {
          partitions: { label: string }[]
        }
      }

      if (allocation.allocationType === "dimensionValue") {
        if (allocation.dimensionValue === virtualDimensionValue) {
          return rule.owner
        }
      } else if (allocation.allocationType === "splitCost") {
        const partition = allocation.reAllocationParams?.partitions.find(
          (p) => p.label === virtualDimensionValue,
        )
        if (partition) {
          return rule.owner
        }
      }
    }
    return null
  }, [
    formData.availableVirtualDimensions,
    virtualDimensionId,
    virtualDimensionValue,
  ])

  const onAllocationChange = (newAllocations: number[]) => {
    onChange({ allocations: newAllocations, ownerId })
  }

  return (
    <>
      {virtualDimensionValue !== "Total" && (
        <tr>
          <td colSpan={18} style={{ backgroundColor: "black" }} />
        </tr>
      )}
      <tr>
        <th rowSpan={4 + extraLines} style={{ padding: 4 }}>
          <Tooltip label="Compare to business metric">
            <ActionIcon onClick={onClickCompareToBusinessMetric}>
              {businessMetricId !== null ? (
                <IconBriefcaseOff />
              ) : (
                <IconBriefcase />
              )}
            </ActionIcon>
          </Tooltip>
        </th>
        <th
          rowSpan={4 + extraLines}
          style={{
            borderRight: mediumGrayBorder,
            padding: 4,
            height: 40,
            maxWidth: 150,
            wordWrap: "break-word",
          }}
        >
          <Group gap="xs" justify="flex-start" wrap="nowrap">
            {vdOwner && (
              <UserAvatar
                fullName={
                  vdOwner.firstName && vdOwner.lastName
                    ? `${vdOwner.firstName} ${vdOwner.lastName}`
                    : "Unknown"
                }
                avatar={vdOwner.avatar}
                size="sm"
              />
            )}
            <Text fw="bold" size="sm">
              {virtualDimensionValue}
            </Text>
          </Group>
        </th>
        {mode !== BudgetTableMode.View &&
          (virtualDimensionValue !== "Total" ? (
            <>
              <AllocationsRow
                allocations={allocations}
                onChange={onAllocationChange}
                currency={currency}
                name={currentRevisionName}
              />
            </>
          ) : (
            <RevisionRow
              name={currentRevisionName}
              allocations={allocations}
              currency={currency}
              status={BudgetStatus.VALIDATED}
            />
          ))}
      </tr>
      {_(revisions)
        .orderBy((a) => a.lastEdited, "desc")
        .value()
        .map(({ name, values, versionStatus, lastEdited }, index) => {
          if (
            (mode === BudgetTableMode.Edit && index === 0) ||
            mode === BudgetTableMode.Create
          ) {
            return null
          }

          return (
            <tr key={index}>
              <RevisionRow
                key={index}
                name={
                  name.length > 0
                    ? name
                    : `V:${dayjs(lastEdited).format("MMM YYYY")}`
                }
                allocations={values}
                currency={currency}
                status={versionStatus}
              />
            </tr>
          )
        })}
      <tr>
        <SectionHead name="Actual" />
        {actualCosts.map((actualCost, index) => (
          <FormatedTd
            key={index}
            style={{ borderRight: index === 11 ? mediumGrayBorder : undefined }}
            data={actualCost}
            currency={currency}
          />
        ))}
        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          data={actualCostsYTD}
          currency={currency}
        />
        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          currency={currency}
          data={actualCostsTotal}
        />
      </tr>
      <tr>
        <SectionHead name="Forecast" />
        {forecastedCosts.map((forecastCost, index) => (
          <FormatedTd
            key={index}
            style={{ borderRight: index === 11 ? mediumGrayBorder : undefined }}
            data={forecastCost}
            currency={currency}
          />
        ))}

        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          data={forecastedCostsYTD}
          currency={currency}
        />
        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          data={forecastedCostsTotal}
          currency={currency}
        />
      </tr>
      <tr>
        <SectionHead name="Delta" />
        {deltas.map((delta, index) => (
          <FormatedTd
            key={index}
            style={{ borderRight: index === 11 ? mediumGrayBorder : undefined }}
            data={delta}
            currency={currency}
            formatForDelta
          />
        ))}
        <FormatedTd
          currency={currency}
          style={{ borderRight: mediumGrayBorder }}
          data={deltasYTD}
          formatForDelta
        />
        <FormatedTd
          style={{ borderRight: mediumGrayBorder }}
          currency={currency}
          data={deltasTotal}
          formatForDelta
        />
      </tr>
      {businessMetricId !== null && (
        <>
          <tr>
            <td colSpan={17} style={{ backgroundColor: "black" }} />
          </tr>
          <BusinessMetricRow
            virtualDimensionValue={virtualDimensionValue}
            virtualDimensionId={virtualDimensionId}
            startDate={startDate}
            formData={formData}
            currency={currency}
            costType={costType}
          />
        </>
      )}
    </>
  )
}
